import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiSearch, FiUser, FiAlertTriangle, FiArrowLeft,
  FiRefreshCw, FiX, FiThumbsUp, FiThumbsDown,
} from 'react-icons/fi';
import '../commissionscan/CommissionScan.css';
import '../homeowners/homeowners.css';
import './vehicleprefill.css';
import { useToast } from '../../context/ToastContext';

const getToken      = () => localStorage.getItem('toolboxToken') || '';
const getNcToken    = () => localStorage.getItem('amsToken')     || '';

type SearchType = '' | 'DOT-Number' | 'License-Plate' | 'Company-Name';

interface Client {
  databaseId: string;
  name: string;
  insuredType?: number;
}

interface VehicleRow {
  uniqueId?: string;
  vinNumber: string;
  vehicleType: string;
  vehicleManufacturer: string;
  check: boolean;
  existingDataInAMSFlag: boolean;
}

interface Props {
  onGoToHistory: () => void;
}

const VehiclePrefillSearch: React.FC<Props> = () => {
  const [clientMode, setClientMode] = useState<'existing' | 'new'>('existing');

  /* Existing client search */
  const [clientSearch, setClientSearch]   = useState('');
  const [clientName, setClientName]       = useState('');
  const [clientDbId, setClientDbId]       = useState('');
  const [clientList, setClientList]       = useState<Client[]>([]);
  const [clientLoading, setClientLoading] = useState(false);
  const [clientError, setClientError]     = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* Search criteria */
  const [selectedType, setSelectedType]   = useState<SearchType>('');
  const [criteriaValue, setCriteriaValue] = useState('');
  const [formErrors, setFormErrors]       = useState<Record<string, string>>({});

  /* Results */
  const [searching, setSearching]     = useState(false);
  const [vehicles, setVehicles]       = useState<VehicleRow[]>([]);
  const [transactionId, setTransactionId] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [noData, setNoData]           = useState(false);
  const [insufficientBalance, setInsufficientBalance] = useState(false);
  const [rateLimited, setRateLimited] = useState(false);

  /* Company info captured off the first search result (used to create a new insured) */
  const [companyName, setCompanyName] = useState('');
  const [address, setAddress]         = useState('');
  const [city, setCity]               = useState('');
  const [stateName, setStateName]     = useState('');
  const [zipCode, setZipCode]         = useState('');

  /* Submit to AMS */
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError]     = useState('');

  /* Feedback */
  const [feedbackText, setFeedbackText]           = useState('');
  const [feedbackRating, setFeedbackRating]       = useState<boolean | null>(null);
  const [feedbackAnchor, setFeedbackAnchor]       = useState<{ top?: number; bottom?: number; right: number } | null>(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedBackError, setFeedBackError]         = useState('');
  const feedbackRef = useRef<HTMLDivElement>(null);

  /* Toast */
  const { showSuccess, showError } = useToast();

  /* ── Client search — debounced ── */
  const fetchClients = async (query: string) => {
    try {
      const res = await axios.get(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/customerlist?searchText=${encodeURIComponent(query)}&nowCertsToken=${getNcToken()}`,
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      setClientList(res.data || []);
    } catch { setClientList([]); }
    finally { setClientLoading(false); }
  };

  useEffect(() => {
    if (!clientSearch.trim() || clientDbId) { setClientList([]); setClientLoading(false); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setClientLoading(true);
    debounceRef.current = setTimeout(() => fetchClients(clientSearch), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [clientSearch, clientDbId]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClientSelect = useCallback((c: Client) => {
    setClientName(c.name);
    setClientDbId(c.databaseId);
    setClientSearch(c.name);
    setClientList([]);
    setClientError('');
    if (selectedType === 'Company-Name') setCriteriaValue(c.name);
  }, [selectedType]);

  const onClientModeChange = (mode: 'existing' | 'new') => {
    setClientMode(mode);
    setClientError('');
    setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]);
    if (mode === 'new' && selectedType === 'Company-Name') setCriteriaValue('');
  };

  /* ── Search criteria ── */
  const onSelectType = (type: SearchType) => {
    setSelectedType(type);
    setFormErrors({});
    if (type === 'Company-Name') setCriteriaValue(clientName);
    else setCriteriaValue('');
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (clientMode === 'existing' && !clientDbId) {
      errs.client = 'Please select a client from the list.';
    }
    if (!selectedType) {
      errs.type = 'Please select a vehicle identification criteria.';
    } else if (!criteriaValue.trim()) {
      errs.value = 'Please enter a value for the selected criteria.';
    } else if (selectedType === 'DOT-Number' && !/^\d+$/.test(criteriaValue.trim())) {
      errs.value = 'Please enter a valid DOT number.';
    } else if (selectedType === 'License-Plate') {
      const [plate, st] = criteriaValue.trim().split('-');
      if (!plate || !/^[A-Za-z0-9]+$/.test(plate) || !st || !/^[A-Za-z]{2}$/.test(st)) {
        errs.value = "Please enter a valid license plate. Example: 'AF60214-PA'.";
      }
    }
    setFormErrors(errs);
    if (errs.client) setClientError(errs.client);
    return Object.keys(errs).length === 0;
  };

  /* ── Search ── */
  const onSearch = useCallback(async () => {
    if (!validate()) return;
    setSearching(true);
    setShowResults(false);
    setNoData(false);
    setInsufficientBalance(false);
    setRateLimited(false);
    setSubmitSuccess(false);
    setSubmitError('');

    try {
      const res = await axios.post(
        `${(process.env.REACT_APP_DataFabric_Url || '')}/api/vehicle/searchvehicledetails`,
        {
          target: 'VEHICLE-CARRIER-SEARCH',
          insured: {
            Name: clientMode === 'existing' ? clientName : '',
            insuredId: clientMode === 'existing' ? clientDbId : '',
          },
          selectedType,
          selectedTypeOfValue: criteriaValue,
          selectedOption: {
            CreateNewClient: clientMode === 'new',
            useExisiting: clientMode === 'existing',
          },
        },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );

      const data = res.data;
      if (data?.success === true) {
        const master = data.vehiclePrefillMaster || [];
        if (master.length > 0) {
          setCompanyName(master[0].companyName || (clientMode === 'existing' ? clientName : ''));
          setAddress(master[0].address || '');
          setCity(master[0].city || '');
          setStateName(master[0].state || '');
          setZipCode(master[0].zipCode || '');

          let rows: VehicleRow[] = master.map((item: any) => ({
            uniqueId: item.uniqueId,
            vinNumber: item.vinNumber,
            vehicleType: item.vehicleType,
            vehicleManufacturer: item.vehicleManufacturer,
            check: false,
            existingDataInAMSFlag: false,
          }));

          if (clientMode === 'existing' && clientDbId) {
            try {
              const existingRes = await axios.get(
                `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/vehicles?insuredDatabaseId=${clientDbId}&ncToken=${getNcToken()}`,
                { headers: { Authorization: `Bearer ${getToken()}` } },
              );
              const existingVins = new Set(
                (existingRes.data?.value || []).filter((v: any) => v.active).map((v: any) => v.vin),
              );
              rows = rows.map(r => existingVins.has(r.vinNumber) ? { ...r, existingDataInAMSFlag: true } : r);
            } catch { /* non-fatal */ }
          }

          setVehicles(rows);
          setTransactionId(data.transactionId || '');
          setShowResults(true);
        } else {
          setNoData(true);
          showSuccess('No Vehicle Prefill details are available.');
        }
      } else if (data?.success === false) {
        if (data?.errorMessage?.includes('enough credits')) {
          setInsufficientBalance(true);
          showError('User does not have enough credits in the current balance.');
        } else {
          showError(data?.errorMessage || 'No Vehicle Prefill details are available.');
          setNoData(true);
        }
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setRateLimited(true);
      } else {
        showError('Unknown error while searching vehicle details.');
      }
    } finally {
      setSearching(false);
    }
  }, [clientMode, clientDbId, clientName, selectedType, criteriaValue]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Selection ── */
  const toggleRow = (vin: string) => {
    setVehicles(prev => prev.map(v => v.vinNumber === vin ? { ...v, check: !v.check } : v));
  };

  const checkedCount = vehicles.filter(v => v.check).length;

  /* ── Submit to AMS ── */
  const onSubmitToAMS = useCallback(async () => {
    if (checkedCount === 0) {
      setSubmitError('Please select at least one vehicle.');
      return;
    }
    setSubmitLoading(true);
    setSubmitError('');

    try {
      let insuredDatabaseId = clientDbId;

      if (clientMode === 'new') {
        const insRes = await axios.post(
          `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/insertinsured?nowCertsToken=${getNcToken()}`,
          {
            commercialName: companyName, firstName: '', lastName: '', dba: '',
            addressLine1: address, addressLine2: '', state: stateName, city,
            zipCode, eMail: '', cellPhone: '', fein: '', insuredType: 0,
          },
          { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
        );
        insuredDatabaseId = insRes.data?.database_id || '';
        if (!insuredDatabaseId) throw new Error('Failed to create insured in AMS.');
      }

      const requestDataList = vehicles.filter(v => v.check).map(v => ({
        insured_database_id: insuredDatabaseId,
        vinNumber: v.vinNumber,
        vehicleManufacturer: v.vehicleManufacturer,
        vehicleType: v.vehicleType,
        year: 0,
        insured_commercial_name: companyName,
        check: true,
      }));

      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/nowcert/addvehicles?ncToken=${getNcToken()}`,
        requestDataList,
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );

      await axios.post(
        `${(process.env.REACT_APP_NOWCERTSAPI    || '')}/api/NowCert/updatevehicle`,
        { transactionId, addVehicleRequests: requestDataList },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      ).catch(() => {});

      setSubmitSuccess(true);
      showSuccess('Vehicle details added successfully in NowCerts AMS.');
    } catch (err: any) {
      setSubmitError(err.response?.data?.errorMessage || err.message || 'Error adding vehicle data in NowCerts.');
    } finally {
      setSubmitLoading(false);
    }
  }, [checkedCount, clientMode, clientDbId, companyName, address, stateName, city, zipCode, vehicles, transactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Feedback ── */
  const closeFeedback = useCallback(() => {
    setFeedbackAnchor(null);
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
  }, []);

  const openFeedbackPopup = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setFeedbackText('');
    setFeedbackRating(null);
    setFeedBackError('');
    const right = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom;
    if (spaceBelow < 320) setFeedbackAnchor({ bottom: window.innerHeight - rect.top + 8, right });
    else setFeedbackAnchor({ top: rect.bottom + 8, right });
  }, []);

  const saveRating = useCallback((isLiked: boolean | null, feedback = '') => {
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/vehicle/isliked`,
      { TransactionId: transactionId, Feedback: feedback, IsLiked: isLiked },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    ).then(() => showSuccess('Feedback submitted successfully!')).catch(() => {});
  }, [transactionId, showSuccess]);

  const onThumbsUpClick = useCallback(() => {
    const next = feedbackRating === true ? null : true;
    closeFeedback();
    saveRating(next, feedbackText);
  }, [feedbackRating, feedbackText, closeFeedback, saveRating]);

  const onThumbsDownClick = useCallback(() => {
    const next = feedbackRating === false ? null : false;
    closeFeedback();
    saveRating(next, feedbackText);
  }, [feedbackRating, feedbackText, closeFeedback, saveRating]);

  const onSubmitFeedbackClick = useCallback(() => {
    if (!feedbackText.trim()) { setFeedBackError('Please enter your feedback'); return; }
    if (feedbackText.length > 250) { setFeedBackError('Please keep feedback under 250 characters'); return; }
    setFeedBackError('');
    setFeedbackSubmitting(true);
    axios.post(`${(process.env.REACT_APP_DataFabric_Url || '')}/api/vehicle/feedback`,
      { TransactionId: transactionId, Feedback: feedbackText },
      { headers: { Authorization: `Bearer ${getToken()}` } },
    )
      .then(() => { setFeedbackSubmitting(false); showSuccess('Feedback submitted successfully!'); closeFeedback(); })
      .catch(() => { setFeedbackSubmitting(false); });
  }, [transactionId, feedbackText, closeFeedback, showSuccess]);

  useEffect(() => {
    if (!feedbackAnchor) return;
    const handler = (e: MouseEvent) => {
      if (feedbackRef.current && !feedbackRef.current.contains(e.target as Node)) closeFeedback();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [feedbackAnchor, closeFeedback]);

  const onReset = () => {
    setClientMode('existing');
    setClientSearch(''); setClientName(''); setClientDbId('');
    setClientList([]); setClientError('');
    setSelectedType(''); setCriteriaValue(''); setFormErrors({});
    setVehicles([]); setShowResults(false); setNoData(false);
    setInsufficientBalance(false); setRateLimited(false);
    setTransactionId('');
    setCompanyName(''); setAddress(''); setCity(''); setStateName(''); setZipCode('');
    setSubmitLoading(false); setSubmitSuccess(false); setSubmitError('');
    closeFeedback();
  };

  /* ════════════ RENDER ════════════ */
  return (
    <>
      <motion.div className="ho-search" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.22 }}>

        {/* ── Search Form ── */}
        {!showResults && !noData && !rateLimited && (
          <div className="ho-card ho-card--form">

            {/* Step 1 — Client */}
            <div className="ho-step-label">
              <span className="ho-step-num">1</span> Client
            </div>
            <div className="ho-toggle-group">
              <button
                className={`ho-toggle${clientMode === 'existing' ? ' ho-toggle--active' : ''}`}
                onClick={() => onClientModeChange('existing')}
              >
                <FiSearch size={14} /> Use Existing Client
              </button>
              <button
                className={`ho-toggle${clientMode === 'new' ? ' ho-toggle--active' : ''}`}
                onClick={() => onClientModeChange('new')}
              >
                <FiUser size={14} /> Create New Client
              </button>
            </div>

            {clientMode === 'existing' && (
              <div className="cs-client-search">
                <div className="cs-client-search__input-wrap" style={{ borderColor: clientError ? 'var(--accent-red, #ef4444)' : undefined }}>
                  <FiSearch size={15} className="cs-client-search__icon" />
                  <input
                    className="cs-client-search__input"
                    type="text"
                    placeholder="Search by client name…"
                    value={clientSearch}
                    onChange={e => {
                      setClientSearch(e.target.value);
                      if (clientError) setClientError('');
                      if (clientName) { setClientName(''); setClientDbId(''); }
                    }}
                  />
                  {clientLoading && <span className="cs-client-search__spinner" />}
                  {clientName && !clientLoading && (
                    <button className="cs-client-search__clear" onClick={() => { setClientSearch(''); setClientName(''); setClientDbId(''); setClientList([]); setClientError(''); }}>
                      <FiX size={14} />
                    </button>
                  )}
                </div>
                {clientList.length > 0 && (
                  <ul className="cs-client-dropdown">
                    {clientList.map(c => (
                      <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => onClientSelect(c)}>
                        <span className="cs-client-dropdown__name">{c.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {clientSearch.trim() && !clientLoading && clientList.length === 0 && !clientName && (
                  <ul className="cs-client-dropdown">
                    <li className="cs-client-dropdown__item vehicle-prefill-search__no-results">No clients found</li>
                  </ul>
                )}
                {clientError && <span className="vehicle-prefill-search__field-error">{clientError}</span>}
              </div>
            )}

            {/* Step 2 — Search Criteria */}
            <div className="ho-step-divider" />
            <div className="ho-step-label">
              <span className="ho-step-num">2</span> Vehicle Identification Criteria
            </div>
            <div className="ho-toggle-group">
              {([
                ['DOT-Number',    'DOT Number'],
                ['License-Plate', 'License Plate'],
                ['Company-Name',  'Company Name'],
              ] as [SearchType, string][]).map(([type, label]) => (
                <button
                  key={type}
                  className={`ho-toggle${selectedType === type ? ' ho-toggle--active' : ''}`}
                  onClick={() => onSelectType(type)}
                >
                  {label}
                </button>
              ))}
            </div>
            {formErrors.type && <span className="vehicle-prefill-search__field-error">{formErrors.type}</span>}

            {selectedType && (
              <div className="ho-form-grid vehicle-prefill-search__grid-spacing">
                <div className="ho-form-field ho-form-field--full">
                  <label className="ho-form-label">
                    {selectedType === 'DOT-Number' ? 'DOT Number' : selectedType === 'License-Plate' ? 'License Plate (e.g. AF60214-PA)' : 'Company Name'}
                  </label>
                  <div className="cs-client-search__input-wrap" style={{ borderColor: formErrors.value ? 'var(--accent-red, #ef4444)' : undefined }}>
                    <input
                      className="cs-client-search__input"
                      type="text"
                      placeholder="Enter value…"
                      value={criteriaValue}
                      maxLength={255}
                      onChange={e => { setCriteriaValue(e.target.value); setFormErrors(f => ({ ...f, value: '' })); }}
                      onKeyDown={e => { if (e.key === 'Enter') onSearch(); }}
                    />
                  </div>
                  {formErrors.value && <span className="vehicle-prefill-search__field-error vehicle-prefill-search__field-error--tight">{formErrors.value}</span>}
                </div>
              </div>
            )}

            <div className="vehicle-prefill-search__search-row">
              <button className="cs-browse-btn" onClick={onSearch} disabled={searching}>
                {searching ? <><FiRefreshCw size={13} className="ho-spin vehicle-prefill-search__spin-margin" />Searching…</> : 'Search Vehicles'}
              </button>
            </div>
          </div>
        )}

        {/* ── Rate limited ── */}
        {rateLimited && (
          <motion.div className="ho-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
            <div className="vehicle-prefill-search__banner vehicle-prefill-search__banner--warning">
              <FiAlertTriangle size={18} className="vehicle-prefill-search__banner-icon--warning" />
              <div>
                <div className="vehicle-prefill-search__banner-title">Rate limit reached</div>
                <div className="vehicle-prefill-search__banner-desc">You're only allowed to run one Vehicle Prefill request per minute. Please try again shortly.</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Insufficient balance ── */}
        {insufficientBalance && (
          <motion.div className="ho-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
            <div className="vehicle-prefill-search__banner vehicle-prefill-search__banner--danger">
              <FiAlertTriangle size={18} className="vehicle-prefill-search__banner-icon--danger" />
              <div>
                <div className="vehicle-prefill-search__banner-title">Insufficient balance</div>
                <div className="vehicle-prefill-search__banner-desc">Your account doesn't have enough credits to run a Vehicle Prefill search.</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── No Data ── */}
        {noData && (
          <motion.div className="ho-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
            <div className="vehicle-prefill-search__banner vehicle-prefill-search__banner--warning">
              <FiAlertTriangle size={18} className="vehicle-prefill-search__banner-icon--warning" />
              <div>
                <div className="vehicle-prefill-search__banner-title">No vehicle prefill data available</div>
                <div className="vehicle-prefill-search__banner-desc">No results were found for this search criteria.</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── Results ── */}
        {showResults && (
          <motion.div className="ho-card" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
            <div className="ho-result-topbar">
              <button className="ho-back-btn" onClick={onReset}><FiArrowLeft size={14} /> Back</button>
              <span className="vehicle-prefill-search__result-count">{vehicles.length} vehicle(s) found · {checkedCount} selected</span>
            </div>

            <div className="qp-table-wrap glass-surface">
              <table className="qp-table">
                <thead>
                  <tr>
                    <th className="qp-table__th vehicle-prefill-search__checkbox-th"></th>
                    <th className="qp-table__th">VIN</th>
                    <th className="qp-table__th">Type</th>
                    <th className="qp-table__th">Make</th>
                    <th className="qp-table__th"></th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((v, i) => (
                    <tr key={v.vinNumber + i} className="qp-table__row">
                      <td className="qp-table__td">
                        <input type="checkbox" checked={v.check} onChange={() => toggleRow(v.vinNumber)} />
                      </td>
                      <td className="qp-table__td vehicle-prefill-search__vin-cell">{v.vinNumber}</td>
                      <td className="qp-table__td">{v.vehicleType}</td>
                      <td className="qp-table__td">{v.vehicleManufacturer}</td>
                      <td className="qp-table__td">
                        {v.existingDataInAMSFlag && (
                          <span className="ho-badge vehicle-prefill-search__badge-sm">Already in NowCerts</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {submitError && <p className="vehicle-prefill-search__submit-error">{submitError}</p>}

            {!submitSuccess ? (
              <div className="vehicle-prefill-search__actions-row">
                <button className="vp-pill-btn" onClick={onSubmitToAMS} disabled={submitLoading}>
                  {submitLoading ? <><FiRefreshCw size={13} className="ho-spin vehicle-prefill-search__spin-margin" />Adding…</> : 'Update in NowCerts'}
                </button>
                <button className="vp-pill-btn" onClick={openFeedbackPopup}>Feedback</button>
                <button className="vp-pill-btn" onClick={onReset}>Cancel</button>
              </div>
            ) : (
              <div className="vehicle-prefill-search__actions-row vehicle-prefill-search__actions-row--center">
                <button className="vp-pill-btn" onClick={openFeedbackPopup}>Feedback</button>
              </div>
            )}
          </motion.div>
        )}
      </motion.div>

      {/* ── Feedback popup ── */}
      {feedbackAnchor && (
        <div
          ref={feedbackRef}
          className="csfb-popup"
          style={{
            top:    feedbackAnchor.top    !== undefined ? feedbackAnchor.top    : 'auto',
            bottom: feedbackAnchor.bottom !== undefined ? feedbackAnchor.bottom : 'auto',
            right:  feedbackAnchor.right,
          }}
        >
          <h4 className="csfb-title">Feedback</h4>
          <textarea
            className="csfb-textarea"
            placeholder="Enter your feedback here"
            value={feedbackText}
            onChange={e => setFeedbackText(e.target.value)}
            rows={4}
          />
          <div className="csfb-rating">
            <button
              className={`csfb-rating-btn${feedbackRating === true ? ' csfb-rating-btn--up' : ''}`}
              onClick={onThumbsUpClick}
              title="Thumbs up"
            >
              <FiThumbsUp size={20} />
            </button>
            <button
              className={`csfb-rating-btn${feedbackRating === false ? ' csfb-rating-btn--down' : ''}`}
              onClick={onThumbsDownClick}
              title="Thumbs down"
            >
              <FiThumbsDown size={20} />
            </button>
          </div>
          {feedBackError && <p className="csfb-error">{feedBackError}</p>}
          <div className="csfb-actions">
            <button className="csfb-btn-cancel" onClick={closeFeedback}>Cancel</button>
            <button
              className="csfb-btn-submit"
              disabled={feedbackSubmitting}
              onClick={onSubmitFeedbackClick}
            >
              {feedbackSubmitting ? 'Submitting…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default VehiclePrefillSearch;

